import { apiFetch } from './client'
import type {
  BarcodeLookupResult,
  ProductListItem,
  ProductRead,
  StockBatchListItem,
  StockBatchRead,
  StockMovementRead,
  StockRegisterRow,
} from '../types/stock'

// ── Products ──────────────────────────────────────────────────────────────────

export async function listProducts(params: {
  practice_id?: number  // omitted: every practice the user can access
  search?: string
  schedule?: number | null
  is_active?: boolean
}): Promise<ProductListItem[]> {
  const qs = new URLSearchParams()
  if (params.practice_id !== undefined) qs.set('practice_id', String(params.practice_id))
  if (params.search) qs.set('search', params.search)
  if (params.schedule != null) qs.set('schedule', String(params.schedule))
  if (params.is_active !== undefined) qs.set('is_active', String(params.is_active))
  const res = await apiFetch(`/api/stock/products?${qs}`)
  if (!res.ok) throw new Error('Failed to load products')
  return res.json() as Promise<ProductListItem[]>
}

export async function getProduct(id: number): Promise<ProductRead> {
  const res = await apiFetch(`/api/stock/products/${id}`)
  if (res.status === 404) throw new Error('Product not found')
  if (!res.ok) throw new Error('Failed to load product')
  return res.json() as Promise<ProductRead>
}

export async function createProduct(data: {
  practice_id: number
  name: string
  generic_name?: string | null
  manufacturer?: string | null
  barcode?: string | null
  unit: string
  schedule?: number | null
  default_price?: string | null
  notes?: string | null
}): Promise<ProductRead> {
  const res = await apiFetch('/api/stock/products', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create product')
  }
  return res.json() as Promise<ProductRead>
}

export async function updateProduct(id: number, data: {
  name?: string | null
  generic_name?: string | null
  manufacturer?: string | null
  barcode?: string | null
  unit?: string | null
  schedule?: number | null
  default_price?: string | null
  is_active?: boolean | null
  notes?: string | null
}): Promise<ProductRead> {
  const res = await apiFetch(`/api/stock/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update product')
  }
  return res.json() as Promise<ProductRead>
}

export async function deleteProduct(id: number): Promise<void> {
  const res = await apiFetch(`/api/stock/products/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to deactivate product')
}

export async function lookupBarcode(practiceId: number, barcode: string): Promise<BarcodeLookupResult> {
  const res = await apiFetch(`/api/stock/products/barcode/${encodeURIComponent(barcode)}?practice_id=${practiceId}`)
  if (!res.ok) throw new Error('Failed to look up barcode')
  return res.json() as Promise<BarcodeLookupResult>
}

// ── Stock Batches ─────────────────────────────────────────────────────────────

export async function listBatches(params: {
  practice_id: number
  product_id?: number
}): Promise<StockBatchListItem[]> {
  const qs = new URLSearchParams({ practice_id: String(params.practice_id) })
  if (params.product_id !== undefined) qs.set('product_id', String(params.product_id))
  const res = await apiFetch(`/api/stock/batches?${qs}`)
  if (!res.ok) throw new Error('Failed to load batches')
  return res.json() as Promise<StockBatchListItem[]>
}

export async function receiveBatch(data: {
  practice_id: number
  product_id: number
  batch_number?: string | null
  expiry_date?: string | null
  quantity: string
  cost_price?: string | null
  notes?: string | null
}): Promise<StockBatchRead> {
  const res = await apiFetch('/api/stock/batches', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to receive stock')
  }
  return res.json() as Promise<StockBatchRead>
}

export async function adjustBatch(id: number, data: {
  quantity: string
  notes?: string | null
  issued_by_signature?: string | null
}): Promise<StockMovementRead> {
  const res = await apiFetch(`/api/stock/batches/${id}/adjust`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to adjust stock')
  }
  return res.json() as Promise<StockMovementRead>
}

// ── Movements ─────────────────────────────────────────────────────────────────

export async function listMovements(params: {
  practice_id: number
  product_id?: number
  batch_id?: number
  movement_type?: string
  date_from?: string
  date_to?: string
  consultation_id?: number
}): Promise<StockMovementRead[]> {
  const qs = new URLSearchParams({ practice_id: String(params.practice_id) })
  if (params.product_id !== undefined) qs.set('product_id', String(params.product_id))
  if (params.batch_id !== undefined) qs.set('batch_id', String(params.batch_id))
  if (params.movement_type) qs.set('movement_type', params.movement_type)
  if (params.date_from) qs.set('date_from', params.date_from)
  if (params.date_to) qs.set('date_to', params.date_to)
  if (params.consultation_id !== undefined) qs.set('consultation_id', String(params.consultation_id))
  const res = await apiFetch(`/api/stock/movements?${qs}`)
  if (!res.ok) throw new Error('Failed to load movements')
  return res.json() as Promise<StockMovementRead[]>
}

export async function createMovement(data: {
  product_id: number
  practice_id: number
  movement_type: string
  quantity: string
  patient_id?: number | null
  client_id?: number | null
  consultation_id?: number | null
  checked_by_id?: number | null
  checked_by_signature?: string | null
  notes?: string | null
}): Promise<StockMovementRead> {
  const res = await apiFetch('/api/stock/movements', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to record movement')
  }
  return res.json() as Promise<StockMovementRead>
}

// ── Register ──────────────────────────────────────────────────────────────────

export async function getRegister(productId: number, params: {
  practice_id: number
  date_from?: string
  date_to?: string
}): Promise<StockRegisterRow[]> {
  const qs = new URLSearchParams({ practice_id: String(params.practice_id) })
  if (params.date_from) qs.set('date_from', params.date_from)
  if (params.date_to) qs.set('date_to', params.date_to)
  const res = await apiFetch(`/api/stock/register/${productId}?${qs}`)
  if (!res.ok) throw new Error('Failed to load drug register')
  return res.json() as Promise<StockRegisterRow[]>
}
